import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, MapPin, Users, Star, Edit2, Eye, Trash2 } from 'lucide-react';
import { propertyAPI, getImageUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  active: { color: '#059669', bg: '#ECFDF5' },
  inactive: { color: '#6B7280', bg: '#F3F4F6' },
  maintenance: { color: '#F59E0B', bg: '#FFFBEB' },
  draft: { color: '#9CA3AF', bg: '#F9FAFB' }
};

const PropertiesPage = () => {
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [view, setView] = useState('grid');

  const fetchProperties = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const res = await propertyAPI.getAll(params);
      setProperties(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProperties(); }, [search, statusFilter, typeFilter]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await propertyAPI.delete(id);
      toast.success('Property deleted');
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Properties</h1>
          <p className="page-subtitle">{pagination.total} properties in your portfolio</p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => navigate('/properties/new')}>
            <Plus size={16} /> Add Property
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search properties..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="maintenance">Maintenance</option>
          <option value="draft">Draft</option>
        </select>
        <select className="form-select" style={{ width: 140 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {['villa', 'cabin', 'apartment', 'house', 'condo', 'resort'].map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {['grid', 'list'].map(v => (
            <button key={v} className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView(v)}>
              {v === 'grid' ? '⊞' : '☰'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : properties.length === 0 ? (
        <div className="empty-state">
          <Building2 size={48} />
          <h3>No properties found</h3>
          <p>Add your first property to get started</p>
          {canManage && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/properties/new')}><Plus size={16} /> Add Property</button>}
        </div>
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {properties.map(p => (
            <div key={p._id} className="card card-sm" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Image */}
              <div style={{ height: 180, background: '#F3F4F6', position: 'relative', overflow: 'hidden' }}>
                {p.images?.find(i => i.isCover)?.url || p.images?.[0]?.url ? (
                  <img src={getImageUrl(p.images.find(i => i.isCover)?.url || p.images[0].url)} alt={p.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Building2 size={40} color="#D1D5DB" />
                  </div>
                )}
                <span className="badge" style={{
                  position: 'absolute', top: 12, left: 12,
                  ...STATUS_COLORS[p.status]
                }}>{p.status}</span>
              </div>
              <div style={{ padding: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 6 }}>{p.title}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6B7280', fontSize: 13, marginBottom: 10 }}>
                  <MapPin size={13} />
                  <span>{p.address?.city}, {p.address?.country}</span>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
                  <span>🛏 {p.bedrooms} bd</span>
                  <span>🚿 {p.bathrooms} ba</span>
                  <span><Users size={12} /> {p.maxGuests}</span>
                  {p.analytics?.averageRating > 0 && <span><Star size={12} color="#F59E0B" fill="#F59E0B" /> {p.analytics.averageRating}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                    ${p.pricing?.basePrice?.toLocaleString()}<span style={{ fontSize: 12, fontWeight: 400, color: '#6B7280' }}>/night</span>
                  </span>
                  <div className="table-actions">
                    <button className="btn btn-sm btn-ghost btn-icon" onClick={() => navigate(`/properties/${p._id}`)}><Eye size={15} /></button>
                    {canManage && <button className="btn btn-sm btn-ghost btn-icon" onClick={() => navigate(`/properties/${p._id}/edit`)}><Edit2 size={15} /></button>}
                    {canManage && <button className="btn btn-sm btn-ghost btn-icon" style={{ color: '#EF4444' }} onClick={() => handleDelete(p._id, p.title)}><Trash2 size={15} /></button>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th><th>Location</th><th>Type</th><th>Beds/Baths</th><th>Price/Night</th><th>Rating</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map(p => (
                  <tr key={p._id}>
                    <td><div style={{ fontWeight: 500 }}>{p.title}</div><div style={{ fontSize: 12, color: '#9CA3AF' }}>{p.slug}</div></td>
                    <td>{p.address?.city}, {p.address?.country}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.type}</td>
                    <td>{p.bedrooms}bd / {p.bathrooms}ba</td>
                    <td style={{ fontWeight: 600 }}>${p.pricing?.basePrice?.toLocaleString()}</td>
                    <td>{p.analytics?.averageRating > 0 ? `⭐ ${p.analytics.averageRating}` : '—'}</td>
                    <td>
                      <span className="badge" style={{ ...STATUS_COLORS[p.status] }}>{p.status}</span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/properties/${p._id}`)}>View</button>
                        {canManage && <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/properties/${p._id}/edit`)}>Edit</button>}
                        {canManage && <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p._id, p.title)}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchProperties(p)}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
